import React from "react";
import { Link } from "react-router-dom";
import { Heart, Trash2, ArrowRight } from "lucide-react";
import { useWishlist } from "../contexts/WishlistContext";
import { useCart, formatINR } from "../contexts/CartContext";
import { toast } from "sonner";

export default function Wishlist() {
  const { items, remove, clear } = useWishlist();
  const { addItem } = useCart();

  if (items.length === 0) {
    return (
      <section className="page-first-section flex items-center justify-center" style={{minHeight:"calc(70vh - 68px)",paddingBottom:"clamp(2.5rem,6vw,6rem)"}} data-testid="wishlist-empty">
        <div className="mt-container text-center">
          <div className="mx-auto w-20 h-20 rounded-full mt-glass flex items-center justify-center mb-6" style={{ border: "1px solid var(--mt-glass-border)" }}>
            <Heart size={26} style={{ color: "var(--mt-text-muted)" }} />
          </div>
          <p className="font-mono text-[0.7rem] tracking-mt uppercase mb-3" style={{ color: "var(--mt-text-muted)" }}>The Wishlist</p>
          <h1 className="font-display font-extralight text-[clamp(2.5rem,6vw,5rem)] leading-[1.08]">
            Nothing saved <span className="italic mt-chrome-text mt-keep-anim">yet.</span>
          </h1>
          <p className="mt-italic-silver text-base mt-6 max-w-md mx-auto">
            Tap the heart on any piece to keep it close. Your favorites live here.
          </p>
          <Link to="/shop" className="mt-btn mt-rainbow-edge inline-flex mt-10">
            Explore Drop 01 <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="page-first-section relative" style={{paddingBottom:"clamp(2.5rem,6vw,6rem)"}} data-testid="wishlist-page">
      <div className="mt-container relative z-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="font-mono text-[0.7rem] tracking-mt uppercase mb-2" style={{ color: "var(--mt-text-muted)" }}>The Wishlist · {items.length} {items.length === 1 ? "piece" : "pieces"}</p>
            <h1 className="font-display font-extralight text-[clamp(2.5rem,6vw,5rem)] leading-[1.08]">
              Saved <span className="italic mt-chrome-text mt-keep-anim">for you.</span>
            </h1>
          </div>
          <button onClick={() => { clear(); toast.success("Wishlist cleared"); }} className="font-mono text-[0.65rem] tracking-mt uppercase opacity-70 hover:opacity-100 transition-opacity" style={{ color: "var(--mt-text-muted)" }}>
            Clear all
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((p, i) => {
            const angle = 120 + ((i * 35) % 120);
            return (
              <article key={p.id} className="mt-glass mt-rainbow-edge rounded-[22px] p-5 group">
                <Link to={`/product/${p.id}`} className="block">
                  <div className="aspect-[4/5] rounded-2xl relative overflow-hidden flex items-center justify-center"
                    style={{ background: `linear-gradient(${angle}deg, #1c1c22 0%, #0a0a0c 55%, #000 100%)` }}>
                    <span aria-hidden="true" className="font-display font-semibold select-none"
                      style={{ color: "#F5F5F5", opacity: 0.2, fontSize: "clamp(4rem,10vw,7rem)", letterSpacing: "-0.06em" }}>
                      MT
                    </span>
                    {p.tag && (
                      <span className="absolute top-3 left-3 mt-glass px-3 py-1 rounded-full font-mono text-[0.6rem] tracking-mt uppercase" style={{ color: "#00AAFF" }}>
                        {p.tag}
                      </span>
                    )}
                  </div>
                </Link>
                <div className="pt-5 flex items-end justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl font-light" style={{ color: "var(--mt-text)" }}>{p.name}</h3>
                    <p className="mt-1 font-mono text-[0.66rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>{p.category}</p>
                    <p className="mt-2 font-body font-bold" style={{ color: "var(--mt-text)" }}>{formatINR(p.price)}</p>
                    <span className="mt-blood-line block mt-2" style={{ width: 32 }} />
                  </div>
                </div>
                <div className="mt-5 flex gap-2">
                  <button
                    onClick={() => { addItem(p); toast.success(`${p.name} added to cart`); }}
                    className="flex-1 mt-btn"
                    data-testid={`wishlist-add-${p.id}`}
                  >
                    Add to Cart
                  </button>
                  <button
                    onClick={() => { remove(p.id); toast.success("Removed"); }}
                    className="w-11 h-11 mt-glass rounded-full flex items-center justify-center"
                    style={{ border: "1px solid var(--mt-glass-border)", color: "var(--mt-text-muted)" }}
                    aria-label="Remove from wishlist"
                    data-testid={`wishlist-remove-${p.id}`}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
